
import { Options, Vue } from "vue-class-component";
import MIcon from "@/components/MIcon.vue";
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MTextField from "@/components/form/MTextField.vue";
import MTextArea from "@/components/form/MTextArea.vue";
import { Student } from "@/entities/student";
import store from "@/store";
import "firebase/firestore";
import "firebase/functions";
import {
  Unit,
  reflectionCollectionKey,
  convertToReflection
} from "@/entities/reflection";
import MSLineChart from "@/components/student/MSLineChart.vue";
import { getUnixFromUid, sortReflectionsByUid } from "@/utils/learning_card";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import { getHeartRateDataWithTimeRange } from "@/api/polar";
import {
  fillMissingTimeRange,
  ReflectionWithTimeRange
} from "@/utils/reflection";
dayjs.locale("ja");

@Options({
  components: {
    MIcon,
    MBaseModal,
    MButton,
    MTextField,
    MSLineChart,
    MTextArea
  },
  emits: ["close"],
  props: {
    student: Object,
    unit: Object,
    canEdit: Boolean
  }
})
export default class MsEditModal extends Vue {
  student!: Student;
  unit: Unit | null = null;
  reflections: ReflectionWithTimeRange[] = [];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    borderWidth: number;
    tension: number;
  }[] = [];
  labels: string[] = [];
  canEdit = false;
  unitName = "";
  graphTitle = "";
  secondRowTitle = "";
  thirdRowTitle = "";
  status: "display" | "teacherReflectionInput" | "heartRate" = "display";
  validUnitReflection = false;
  unitReflection = "";
  isTeacherReflectionEmpty = false;
  min = 0;
  max = 100;

  get studentHasPolarDevice(): boolean {
    return !!this.student?.data.polarDeviceId;
  }

  targetHeartRateData: {
    time: number;
    hr: number;
  }[] = [];

  get targetHeartRatesLabels() {
    if (!this.targetHeartRateData) return [];
    return this.targetHeartRateData.map(hr =>
      dayjs.unix(hr.time).format("HH:mm:ss")
    );
  }

  get targetHeartRatesDatasets() {
    if (!this.targetHeartRateData) return [];

    return [
      {
        label: "心拍数",
        data: this.targetHeartRateData.map(hr => hr.hr),
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 2,
        tension: 0.1
      }
    ];
  }

  get heartRateTitle(): string {
    return this.targetHeartRateData.length > 0
      ? `心拍数の推移 ${dayjs
          .unix(this.targetHeartRateData[0].time)
          .format("YYYY/MM/DD HH:mm:ss")} ～ ${dayjs
          .unix(
            this.targetHeartRateData[this.targetHeartRateData.length - 1].time
          )
          .format("YYYY/MM/DD HH:mm:ss")}`
      : "";
  }

  get targetHeartRatesMin() {
    if (!this.targetHeartRateData) return 0;

    const buffer = 5;
    return Math.min(...this.targetHeartRateData.map(_ => _.hr)) - buffer;
  }

  get targetHeartRatesMax() {
    if (!this.targetHeartRateData) return 0;

    const buffer = 5;
    return Math.max(...this.targetHeartRateData.map(_ => _.hr)) + buffer;
  }

  close() {
    this.$emit("close");
  }

  formatDate(uid: string): string {
    const unix = getUnixFromUid(uid);
    return dayjs.unix(unix).format("MM/DD(ddd)");
  }

  async getReflections() {
    if (!this.unit) return [];
    const snapshot = await this.unit.ref
      .collection(reflectionCollectionKey)
      .get();
    return !snapshot.empty
      ? snapshot.docs
          .filter(doc => doc.exists && doc.data())
          .map(doc => convertToReflection(doc.data(), doc.ref))
      : [];
  }

  async openHeartRateData(reflectionId: string) {
    if (!reflectionId) return;
    if (!this.reflections.length) return;
    if (!this.student) return;
    const targetReflection = this.reflections.find(
      r => r.data.uid === reflectionId
    );
    if (!targetReflection) {
      alert(`${reflectionId}の記録が見つかりません。`);
      return;
    }

    try {
      store.commit("START_LOADING", "データ取得中...");

      const { timeRange } = targetReflection.data;
      const res = await getHeartRateDataWithTimeRange(this.student.ref.id, {
        from: timeRange.start,
        to: timeRange.end
      });

      if (!res.history) {
        this.targetHeartRateData = [];
        this.status = "heartRate";
        return;
      }

      this.targetHeartRateData = Object.entries(res.history).map(
        ([time, hr]) => ({
          time: dayjs(time).unix(),
          hr
        })
      );

      this.status = "heartRate";
    } catch (e) {
      alert(
        "データの取得に失敗しました。" +
          "\n" +
          (e instanceof Error ? e.message : "unknown error")
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  clearHeartRateData() {
    this.targetHeartRateData = [];
    this.status = "display";
  }

  openReflectionInput() {
    this.unitReflection = !this.isTeacherReflectionEmpty
      ? ((this.unit as Unit).data.teacherReflection as string)
      : "";
    this.validUnitReflection = this.unitReflection.length > 0 ? true : false;
    this.status = "teacherReflectionInput";
  }

  clearUnitReflectionInput() {
    this.unitReflection = "";
    this.validUnitReflection = false;
    this.status = "display";
  }

  async registerUnitReflection() {
    if (!this.unit) return;
    if (!this.validUnitReflection) return;
    if (this.isTeacherReflectionEmpty && this.unitReflection.length === 0) {
      alert("振り返りが入力されていません。");
      return;
    }
    const text = this.isTeacherReflectionEmpty ? "登録" : "更新";
    try {
      store.commit("START_LOADING", `先生の振り返り${text}中...`);
      await this.unit.ref.update({ teacherReflection: this.unitReflection });
      store.commit("END_LOADING");
      this.$router.go(0);
    } catch (e) {
      alert(`振り返りの${text}に失敗しました。`);
      console.error(e);
      store.commit("END_LOADING");
    }
  }

  async created(): Promise<void> {
    if (!this.student) {
      alert("生徒情報が取得できません。");
      this.$emit("close");
      return;
    }
    if (!this.unit) {
      alert("単元情報が取得できません。");
      this.$emit("close");
      return;
    }

    try {
      store.commit("START_LOADING", "情報取得中...");
      this.unitName = this.unit.data.name;
      this.graphTitle = this.unit.data.graphTitle || "記録(数値)";
      this.secondRowTitle =
        this.unit.data.secondRowTitle || "授業ごとの振り返り";
      this.thirdRowTitle = this.unit.data.thirdRowTitle || "自由入力";

      this.isTeacherReflectionEmpty = this.unit.data.teacherReflection
        ? false
        : true;
      const _reflections = await this.getReflections();
      this.reflections = sortReflectionsByUid(_reflections).map(r =>
        fillMissingTimeRange(r)
      );

      if (this.reflections.length > 0) {
        const records = this.reflections.map(_ => +_.data.record);
        if (this.reflections.length > 1) {
          this.min = Math.min(...records) - 1;
          this.max = Math.max(...records) + 1;
        } else {
          const record = +this.reflections[0].data.record;
          this.min = record < 0 ? record - 1 : 0;
          this.max = record + 1;
        }
      }

      this.labels =
        this.reflections.length > 0
          ? this.reflections.map(_ =>
              dayjs.unix(getUnixFromUid(_.data.uid)).format("MM/DD")
            )
          : [];
      this.datasets =
        this.reflections.length > 0
          ? [
              {
                label: this.unitName,
                data: this.reflections.map(_ => _.data.record),
                borderColor: "rgb(75, 192, 192)",
                borderWidth: 2,
                tension: 0.1
              }
            ]
          : [];
      store.commit("END_LOADING");
    } catch (e) {
      alert("情報の取得に失敗しました。");
      console.error(e);
      store.commit("END_LOADING");
    }
  }
}
